/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react/jsx-no-target-blank */
import Box from "@mui/material/Box";
import React, { FC, useEffect, useMemo } from "react";
import { logEvent } from "firebase/analytics";
import { analytics, auth } from "../../firebase";
import { usePaymentContext } from "../../contexts/PaymentContext";
import { useLocation, useHistory } from "react-router-dom";
import { CircularProgress, Divider, Typography } from "@mui/material";
import theme, { DCButton } from "../../assets/theme/theme";
import constants from "../../constants";
import { postData } from "../../utils/fetchUtils";
import { useAuth } from "../../contexts/AuthContext";
import { isSubscriptionActive } from "../../utils/userUtils";
import { useUserContext } from "../../contexts/UserContext";
import { ActiveSubscription } from "./ActiveSubscription";
import { differenceInDays, format } from "date-fns";


type StripeStatusType = "success" | "cancel" | null;

interface Props { }
const Payment: FC<Props> = () => {
  const { search } = useLocation();
  const history = useHistory();
  const { currentUser } = useAuth();
  const { user } = useUserContext();
  const [loadingManageBilling, setLoadingManageBilling] = React.useState(false);
  const currentTier = user?.tier || "FREE";

  const { status, paymentId } = useMemo<{
    status: StripeStatusType;
    paymentId: string | null;
  }>(() => {
    const query = new URLSearchParams(search);
    return {
      status: query.get("status") as StripeStatusType,
      paymentId: query.get("paymentId"),
    };
  }, [search]);

  const { currentSubscriptionStatus } = usePaymentContext();

  const onClickManageBillingInfo = React.useCallback(async () => {
    if (!currentUser?.uid) return;
    try {
      setLoadingManageBilling(true);
      const url = `${constants.middleHost}/stripe/create-customer-portal-session`;
      const data = {
        uid: currentUser?.uid,
      };
      const token = await currentUser?.getIdToken();
      const headers = {
        Authorization: `Bearer ${token}`,
      };
      const res = await postData(url, data, headers);
      setLoadingManageBilling(false);
      window.open(res.data.url, "_blank");
      window.close();
    } catch (error: any) {
      console.error(error);
      setLoadingManageBilling(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (status && paymentId) {
      history.replace({
        search: "",
      });

      if (status === "success") {
        logEvent(analytics, "begin_checkout", {
          type: "premium",
          status: "success",
        });
      } else if (status === "cancel") {
        logEvent(analytics, "begin_checkout", {
          type: "premium",
          status: "cancel",
        });
      }
    }
  }, [status, paymentId, history]);

  if (isSubscriptionActive(user)) {
    return <ActiveSubscription />;
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", my: 2, flexDirection: "column", gap: 2, alignItems: "center" }}>
      <Box sx={{ border: 1, borderColor: theme.palette.grey[300], borderRadius: 2, p: 4, width: 600, backgroundColor: theme.palette.common.white, display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography
          fontWeight={500}
          variant="h4"
          mb={2}
          sx={{ color: theme.palette.primary.main }}
        >
          User Details{" "}
        </Typography>
        <Box>
          <Box sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            width: "fit-content",
            paddingY: 1,
            paddingX: 2,
            marginLeft: -5,
            borderTopLeftRadius: 2,
            borderTopRightRadius: 2,
            borderBottomRightRadius: 2,
          }}>
            Member since {format(new Date(auth?.currentUser?.metadata.creationTime as string), 'PP')}
          </Box>
          <Box sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            width: 7,
            height: 7,
            marginLeft: -5,
            borderBottomLeftRadius: 2,
          }}>
          </Box>
        </Box>
        <Typography
          fontWeight={500}
          fontSize={16}
        >
          <b>Name :</b> {user?.firstName} {user?.lastName}
        </Typography>
        <Typography
          fontSize={16}
          fontWeight={500}
        >
          <b>Email :</b> {user?.email}
        </Typography>
        <Typography
          fontSize={16}
          fontWeight={500}
          mb={1}
        >
          <b>Phone Number :</b>  {user?.phoneNumber}
        </Typography>
      </Box>
      <Box sx={{ border: 1, borderColor: theme.palette.grey[300], borderRadius: 2, p: 4, width: 600, backgroundColor: theme.palette.common.white, display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography
          fontWeight={500}
          variant="h4"
          mb={2}
          sx={{ color: theme.palette.primary.main }}
        >
          Subscription Details{" "}
        </Typography>
        <Typography
          fontWeight={500}
          fontSize={16}
        >
          <b>Subscription Plan :</b> {currentTier ? `${currentTier.charAt(0).toUpperCase() + currentTier.slice(1).toLocaleLowerCase()} Plan` : "Free Plan"}
        </Typography>
        {(currentTier === "BASIC" || currentTier === "PREMIUM") ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              fontWeight={500}
              fontSize={16}
            >
              <b>Price :</b>  {currentSubscriptionStatus?.currency?.toUpperCase() === "USD" ? "$" : null}{`${parseFloat(currentSubscriptionStatus?.price as string) || "0.00"}`} {currentSubscriptionStatus?.currency?.toUpperCase() !== "USD" ? currentSubscriptionStatus?.currency?.toUpperCase() : ""}
            </Typography>
            <Typography
              fontWeight={500}
              fontSize={16}
            >
              <b>Payment Date :</b> {format((currentSubscriptionStatus?.purchased_at as number) * 1000, "PP hh:mm a")}
            </Typography>
            <Typography
              fontWeight={500}
              fontSize={16}
            >
              <b>Expiration Date :</b> {format((currentSubscriptionStatus?.expiry as number) * 1000, "PP hh:mm a")}
            </Typography>
            <Typography
              fontWeight={500}
              fontSize={16}
            >
              <b>Duration :</b> {
                differenceInDays((currentSubscriptionStatus?.expiry as number) * 1000, new Date()) +
                1 +
                " days left"
              }
            </Typography>
            <Divider sx={{ my: 2 }} />
            {currentSubscriptionStatus?.mode === "Stripe" ? (
              <Typography
                aria-label="manage billing info here"
                fontSize={16}
                color={theme.palette.primary.main}
                onClick={onClickManageBillingInfo}
                sx={{
                  cursor: "pointer",
                }}
              >
                Upgrade or Manage Subscription and Billing Info
                <CircularProgress
                  size={16}
                  sx={{
                    ml: 1,
                    visibility: loadingManageBilling ? "visible" : "hidden",
                  }}
                />
              </Typography>
            ) : (
              <Typography
                fontSize={16}
                bgcolor={theme.palette.primary.light}
                color={theme.palette.primary.main}
                p={2}
                borderRadius={1}
              >
                <b>Note:</b> This subscription was purchased via the mobile app. To make changes, please manage it from the Immplify mobile app.
              </Typography>
            )}
            <a
              color={theme.palette.primary.main}
              href="https://www.immplify.com/pricing"
              target="_blank"
              style={{
                textDecoration: "none",
                cursor: "pointer",
                marginTop: 10,
                fontSize: 16
              }}
            >
              Checkout our pricing plans &#8599;
            </a>
          </Box>
        ) : (
          <DCButton
            variant="contained"
            color="primary"
            sx={{ width: "fit-content", mt: 2 }}
            onClick={() => history.push("/subscription-and-billing/upgrade-plan")}
          >
            Upgrade Plan
          </DCButton>
        )}
      </Box>
    </Box>
  );
};

export default Payment;
